import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

import { WidgetTypes } from "legacy/constants/WidgetConstants";
import { getWidgets } from "legacy/selectors/entitiesSelector";
import { CUSTOMER_UIBLOCKS_CATEGORY_ID } from "pages/Editors/UiBlocks/InsertUIBlocksModal/constants";
import { Flag } from "store/slices/featureFlags";
import { useListUiBlockCategoriesQuery } from "store/slices/reduxApi/uiBlocks";
import type {
  UIBlockCategory,
  UIBlockConfigWithCategory,
} from "store/slices/uiBlocks/types";
import { findFirstParent } from "utils/findFirstParent";

import { useFeatureFlag } from "./useFeatureFlag";

export const useUiBlocksWithCategory = () => {
  const showPending = useFeatureFlag(Flag.UI_BLOCKS_ADMINISTRATION);
  const backendCategoriesResp = useListUiBlockCategoriesQuery(
    { showPending },
    undefined,
  );

  const onRefresh = useCallback(async () => {
    if (!backendCategoriesResp.isUninitialized) {
      await backendCategoriesResp.refetch();
    }
  }, [backendCategoriesResp]);

  return useMemo<{
    uiBlocksWithCategory: UIBlockConfigWithCategory[];
    uiBlocksCategories: UIBlockCategory[];
    isLoadingBlocks: boolean;
    isLoadingCategories: boolean;
    error?: unknown;
    onRefresh: () => void;
  }>(() => {
    const uiBlocksCategories: UIBlockCategory[] = (
      backendCategoriesResp.data ?? []
    ).filter((category) => category.id !== CUSTOMER_UIBLOCKS_CATEGORY_ID);

    const uiBlocksWithCategory: UIBlockConfigWithCategory[] =
      uiBlocksCategories.reduce((acc, category) => {
        return acc.concat(
          category.blocks.map((block) => ({
            ...block,
            categoryId: category.id,
          })),
        );
      }, [] as UIBlockConfigWithCategory[]);

    return {
      uiBlocksWithCategory,
      isLoadingBlocks: backendCategoriesResp.isLoading,
      isLoadingCategories: backendCategoriesResp.isLoading,
      uiBlocksCategories,
      error: backendCategoriesResp.error,
      onRefresh,
    };
  }, [
    onRefresh,
    backendCategoriesResp.data,
    backendCategoriesResp.isLoading,
    backendCategoriesResp.error,
  ]);
};

export const useShowUIBlockPrompt = (widgetId: undefined | string | null) => {
  const isBlocksEnabled = useFeatureFlag(Flag.UI_BLOCKS_FOR_USERS);
  const widgets = useSelector(getWidgets);
  // Grid widgets will almost never be able to accept a UI block since they don't support nestable widgets
  const isWithinGridWidget = useMemo(
    () =>
      widgetId &&
      findFirstParent(
        widgetId,
        widgets,
        (widget) => widget.type === WidgetTypes.GRID_WIDGET,
      ) != null,
    [widgetId, widgets],
  );
  return isBlocksEnabled && !isWithinGridWidget;
};
